import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { toast } from "sonner";
import { Wine } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [role, setRole] = useState("winery");
    const [form, setForm] = useState({
        name: "", email: "", password: "",
        winery_name: "", region: "Mendoza", established_year: "", story: "",
        logo_url: "", banner_url: "",
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form, role };
            if (form.established_year) payload.established_year = Number(form.established_year);
            else delete payload.established_year;
            if (role !== "winery") {
                delete payload.winery_name; delete payload.region; delete payload.established_year;
                delete payload.story; delete payload.logo_url; delete payload.banner_url;
            }
            const res = await register(payload);
            if (role === "winery") {
                toast.success("Application submitted. We'll review and approve within 24 hours.");
                navigate("/dashboard");
            } else {
                toast.success("Account created.");
                navigate("/wines");
            }
        } catch (err) {
            toast.error(formatError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <header className="px-6 md:px-12 lg:px-16 py-6 border-b border-[#27272A] flex justify-between items-center">
                <Link to="/" className="flex items-center gap-2">
                    <Wine className="w-5 h-5 text-[#C9A961]" strokeWidth={1.5} />
                    <span className="font-display text-2xl">CellarGlobal</span>
                </Link>
                <Link to="/login" className="text-sm hover:text-[#C9A961]">Sign in instead →</Link>
            </header>

            <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
                <p className="overline">Join</p>
                <h1 className="font-display text-5xl md:text-6xl mt-3 mb-4">Create your account.</h1>
                <p className="text-base text-[#A1A1AA] mb-12">Apply as a winery for verification, or join as an importer to request quotes.</p>

                <div className="flex gap-2 mb-12 border-b border-[#27272A]" data-testid="role-tabs">
                    {[{ id: "winery", label: "I'm a Winery" }, { id: "importer", label: "I'm an Importer" }].map((r) => (
                        <button
                            key={r.id}
                            onClick={() => setRole(r.id)}
                            data-testid={`role-${r.id}`}
                            className={`px-6 py-4 text-sm tracking-[0.15em] uppercase transition-colors relative ${role === r.id ? "text-[#C9A961]" : "text-[#71717A] hover:text-white"}`}
                        >
                            {r.label}
                            {role === r.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#C9A961]" />}
                        </button>
                    ))}
                </div>

                <form onSubmit={submit} className="space-y-8" data-testid="register-form">
                    <div className="grid md:grid-cols-2 gap-8">
                        <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
                        <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="reg-email" />
                        <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="reg-password" />
                    </div>

                    {role === "winery" && (
                        <>
                            <div className="border-t border-[#27272A] pt-8">
                                <p className="overline mb-6">Winery Details</p>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <Field label="Winery Name" value={form.winery_name} onChange={(v) => setForm({ ...form, winery_name: v })} testid="reg-winery-name" />
                                    <Field label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="reg-region" />
                                    <Field label="Established Year" type="number" value={form.established_year} onChange={(v) => setForm({ ...form, established_year: v })} testid="reg-year" />
                                    <Field label="Logo URL (optional)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="reg-logo" required={false} />
                                </div>
                                <div className="mt-8">
                                    <Field label="Banner Image URL (optional)" value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} testid="reg-banner" required={false} />
                                </div>
                                <div className="mt-8">
                                    <label className="overline block mb-2">Story</label>
                                    <textarea className="input-line resize-none" rows="5" placeholder="Tell us about your winery, terroir, and philosophy..." value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="reg-story" />
                                </div>
                            </div>
                            <p className="text-xs text-[#71717A]">Your application will be reviewed by our team. You'll receive access to the dashboard immediately but your profile won't be public until approved.</p>
                        </>
                    )}

                    <button type="submit" disabled={loading} className="btn-gold w-full" data-testid="register-submit">
                        {loading ? "Creating..." : (role === "winery" ? "Submit Application" : "Create Account")}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid, required = true }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            <input required={required} type={type} className="input-line" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
